import { SEO } from "../components/SEO";
import { PageHero } from "../components/PageHero";
import { Section, SectionHeader } from "../components/Section";
import { Card } from "../components/Card";
import { CTASection } from "../components/CTASection";
import { useTranslation } from "../lib/i18n";

const LEVEL1_TEACHES = [
  "Sentinel Engineering Architecture",
  "Monitoring \u2192 Diagnostics \u2192 Risk Classification \u2192 Intervention \u2192 Verification \u2192 Reporting",
  "Multi-domain safety principles",
  "Sentinel templates and tools",
  "Designing Sentinel-compliant safety behavior",
];

const LEVEL1_AUDIENCE =
  "Schools, hospitality, culinary, healthcare, law enforcement, manufacturing, transportation, defense, municipal agencies, nonprofits: anyone responsible for safety or operations.";

const INDUSTRY_MODULES = [
  { name: "Sentinel for Schools", image: "https://images.pexels.com/photos/30731144/pexels-photo-30731144.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Hospitality", image: "https://images.pexels.com/photos/7821349/pexels-photo-7821349.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Culinary", image: "https://images.pexels.com/photos/15671373/pexels-photo-15671373.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Healthcare", image: "https://images.pexels.com/photos/6129691/pexels-photo-6129691.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Law Enforcement", image: "https://images.pexels.com/photos/7714759/pexels-photo-7714759.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Manufacturing", image: "https://images.pexels.com/photos/7178310/pexels-photo-7178310.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Transportation", image: "https://images.pexels.com/photos/38169158/pexels-photo-38169158.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Defense", image: "https://images.pexels.com/photos/12357501/pexels-photo-12357501.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Municipal Agencies", image: "https://images.pexels.com/photos/31283302/pexels-photo-31283302.jpeg?auto=compress&cs=tinysrgb&w=600" },
  { name: "Sentinel for Nonprofits", image: "https://images.pexels.com/photos/6647027/pexels-photo-6647027.jpeg?auto=compress&cs=tinysrgb&w=600" },
];

const PROFESSIONAL_ROLES = [
  {
    title: "Sentinel Consultant",
    description:
      "Designs Sentinel systems for organizations: environment analysis, risk identification, system design, template customization, staff training, compliance mapping.",
  },
  {
    title: "Sentinel Architect",
    description:
      "Engineering-grade role: full system design, multi-domain integration, product integration, OEM licensing, advanced diagnostics, autonomous intervention logic.",
  },
  {
    title: "Sentinel Auditor",
    description:
      "Verifies Sentinel compliance: audits, diagnostic log review, intervention logic validation, organizational certification.",
  },
  {
    title: "Sentinel Instructor",
    description:
      "Teaches Sentinel courses: curriculum delivery, training methods, certification evaluation.",
  },
];

const PARTNER_ORG_TYPES = [
  "Schools",
  "Hospitals",
  "Hotels",
  "Restaurants",
  "Police departments",
  "Factories",
  "Transportation systems",
  "Municipal agencies",
  "Nonprofits",
];

const PARTNER_INCLUDES = [
  "Sentinel system design",
  "Staff training",
  "Compliance verification",
  "Sentinel-Certified Professionals on staff",
  "Sentinel-compliant workflows",
];

function LevelBadge({ number, label }: { number: string; label: string }) {
  return (
    <div
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: "0.625rem",
        padding: "0.5rem 1rem",
        borderRadius: "var(--radius-full)",
        background: "linear-gradient(135deg, var(--accent-500), var(--accent-700))",
        color: "var(--neutral-50)",
        fontWeight: 700,
        fontSize: "0.8125rem",
        letterSpacing: "0.03em",
        marginBottom: "1.25rem",
      }}
    >
      <span
        style={{
          width: "24px",
          height: "24px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.2)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          fontSize: "0.75rem",
          fontWeight: 800,
        }}
      >
        {number}
      </span>
      {label}
    </div>
  );
}

function CheckItem({ children }: { children: React.ReactNode }) {
  return (
    <li style={{ display: "flex", gap: "0.625rem", alignItems: "flex-start" }}>
      <span
        style={{
          color: "var(--accent-500)",
          flexShrink: 0,
          marginTop: "0.125rem",
          fontWeight: 700,
        }}
      >
        &#10003;
      </span>
      <span style={{ fontSize: "0.9375rem", lineHeight: 1.6 }}>{children}</span>
    </li>
  );
}

export default function CertificationPage() {
  const { t } = useTranslation();
  return (
    <>
      <SEO
        title="Certification Pathways: Sentinel"
        description="Sentinel offers a complete professional ecosystem for individuals and organizations adopting the Sentinel Safety Architecture. Level 1 Core Certification, Level 2 Industry Specialist Modules, Level 3 Professional Roles, and Organizational Partner Certification."
        canonical="/certification"
        image="https://images.pexels.com/photos/37202771/pexels-photo-37202771.jpeg?auto=compress&cs=tinysrgb&w=1200"
      />
      <PageHero
        eyebrow={t("certPath.eyebrow")}
        title={t("certPath.title")}
        description={t("certPath.description")}
        bgVariant="graphite"
        bgImage="https://images.pexels.com/photos/37202771/pexels-photo-37202771.jpeg?auto=compress&cs=tinysrgb&w=1600"
      />

      {/* Intro statement */}
      <Section>
        <div
          style={{
            maxWidth: "720px",
            margin: "0 auto",
            textAlign: "center",
          }}
        >
          <p
            className="lead"
            style={{
              fontSize: "1.25rem",
              lineHeight: 1.6,
              color: "var(--text)",
            }}
          >
            Sentinel is a new engineering architecture, but you do not need to
            be an engineer to use it. Anyone responsible for safety, operations,
            compliance, or system design can become Sentinel-Certified.
          </p>
        </div>
      </Section>

      {/* Level 1 */}
      <Section variant="subtle">
        <div style={{ maxWidth: "760px", margin: "0 auto" }}>
          <LevelBadge number="1" label={t("certPath.level1Badge")} />
          <h2 style={{ marginBottom: "0.75rem" }}>
            {t("certPath.level1Title")}
          </h2>
          <p
            className="text-muted"
            style={{ fontSize: "1.0625rem", marginBottom: "2rem" }}
          >
            {t("certPath.level1Desc")}
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "2rem",
            }}
          >
            <div>
              <h3
                style={{
                  fontSize: "1rem",
                  textTransform: "uppercase",
                  letterSpacing: "0.06em",
                  color: "var(--neutral-600)",
                  marginBottom: "1rem",
                }}
              >
                {t("certPath.level1Teaches")}
              </h3>
              <ul style={{ display: "flex", flexDirection: "column", gap: "0.625rem" }}>
                {LEVEL1_TEACHES.map((item) => (
                  <CheckItem key={item}>{item}</CheckItem>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Section>

      {/* Level 2 */}
      <Section>
        <SectionHeader
          eyebrow={t("certPath.level2Eyebrow")}
          title={t("certPath.level2Title")}
          description={t("certPath.level2Desc")}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))",
            gap: "1.5rem",
            marginBottom: "2.5rem",
          }}
        >
          {INDUSTRY_MODULES.map((mod, i) => (
            <Card key={mod.name} hover style={{ overflow: "hidden", padding: 0 }}>
              <div
                style={{
                  position: "relative",
                  height: "180px",
                  overflow: "hidden",
                }}
              >
                <img
                  src={mod.image}
                  alt={mod.name}
                  loading="lazy"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "cover",
                    transition: "transform 400ms cubic-bezier(0.4,0,0.2,1)",
                  }}
                />
                <div
                  style={{
                    position: "absolute",
                    inset: 0,
                    background:
                      "linear-gradient(180deg, rgba(10,22,40,0.1) 0%, rgba(10,22,40,0.65) 100%)",
                  }}
                />
                <span
                  style={{
                    position: "absolute",
                    top: "0.75rem",
                    left: "0.75rem",
                    width: "32px",
                    height: "32px",
                    borderRadius: "var(--radius-md)",
                    background: "rgba(255,255,255,0.92)",
                    color: "var(--accent-600)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "0.875rem",
                  }}
                >
                  {String(i + 1).padStart(2, "0")}
                </span>
              </div>
              <div style={{ padding: "1.25rem 1.25rem 1.5rem" }}>
                <span style={{ fontWeight: 600, fontSize: "1rem" }}>{mod.name}</span>
              </div>
            </Card>
          ))}
        </div>
        <div
          style={{
            padding: "1.5rem 2rem",
            borderRadius: "var(--radius-lg)",
            background: "var(--bg-subtle)",
            border: "1px solid var(--border)",
          }}
        >
          <h3
            style={{
              fontSize: "1rem",
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              color: "var(--neutral-600)",
              marginBottom: "0.5rem",
            }}
          >
            {t("certPath.level2LearnTitle")}
          </h3>
          <p style={{ fontSize: "0.9375rem", color: "var(--text-muted)" }}>
            {t("certPath.level2LearnDesc")}
          </p>
        </div>
      </Section>

      {/* Level 3 */}
      <Section variant="subtle">
        <SectionHeader
          eyebrow={t("certPath.level3Eyebrow")}
          title={t("certPath.level3Title")}
          description={t("certPath.level3Desc")}
        />
        <div className="grid grid-2">
          {PROFESSIONAL_ROLES.map((role) => (
            <Card key={role.title} hover>
              <h3 style={{ fontSize: "1.25rem", marginBottom: "0.625rem" }}>
                {role.title}
              </h3>
              <p className="text-muted" style={{ fontSize: "0.9375rem", lineHeight: 1.7 }}>
                {role.description}
              </p>
            </Card>
          ))}
        </div>
      </Section>

      {/* Organizational Certification */}
      <Section>
        <SectionHeader
          eyebrow={t("certPath.orgEyebrow")}
          title={t("certPath.orgTitle")}
          description={t("certPath.orgDesc")}
        />
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
            gap: "2rem",
          }}
        >
          <div>
            <h3
              style={{
                fontSize: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--neutral-600)",
                marginBottom: "1rem",
              }}
            >
              {t("certPath.orgWhoFor")}
            </h3>
            <ul style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {PARTNER_ORG_TYPES.map((org) => (
                <CheckItem key={org}>{org}</CheckItem>
              ))}
            </ul>
          </div>
          <div>
            <h3
              style={{
                fontSize: "1rem",
                textTransform: "uppercase",
                letterSpacing: "0.06em",
                color: "var(--neutral-600)",
                marginBottom: "1rem",
              }}
            >
              {t("certPath.orgIncludes")}
            </h3>
            <ul style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
              {PARTNER_INCLUDES.map((item) => (
                <CheckItem key={item}>{item}</CheckItem>
              ))}
            </ul>
          </div>
        </div>
      </Section>

      {/* Why It Matters */}
      <Section variant="dark">
        <SectionHeader
          eyebrow={t("certPath.whyEyebrow")}
          title={t("certPath.whyTitle")}
          dark
        />
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <p
            className="lead lead-dark"
            style={{ marginBottom: "1.5rem", fontSize: "1.125rem" }}
          >
            {t("certPath.whyP1")}
          </p>
          <p
            className="lead lead-dark"
            style={{ fontSize: "1.125rem" }}
          >
            {t("certPath.whyP2")}
          </p>
        </div>
      </Section>

      {/* CTA */}
      <CTASection
        title={t("certPath.ctaTitle")}
        description={t("certPath.ctaDesc")}
        primaryLabel={t("certPath.ctaPrimary")}
        primaryTo="/register"
        secondaryLabel={t("certPath.ctaSecondary")}
        secondaryTo="/downloads"
      />
    </>
  );
}
